/**
 * RACKET-LAB-FRONT-1 — RacketLabTopBar (Jun 2026).
 *
 * Sticky top bar for the authed Racket Lab app shell at `/app/racket-lab/*`.
 * Mirrors `AdminTopBar` visually but with RL-specific quick links + scope
 * pill (organisation / shop) + Preview/QA mode indicator. Read-only:
 * never writes, never calls beyond the cached `useRLContext` hit.
 */
import React from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/i18n";
import LanguageDropdown from "@/components/nav/LanguageDropdown";
import useRLContext from "@/hooks/useRLContext";
import Logo from "@/components/Logo";
import {
  Home, Grid3x3, CalendarDays, LogOut, UserCircle2, Wrench, Inbox,
  Plus, Settings as SettingsIcon, ShieldAlert,
} from "lucide-react";

const STAFF_ROLE_KEYS = new Set([
  "admin", "staff", "qa_manager", "qa_tester", "institutional_observer",
]);

const identityRole = (user, t) => {
  if (!user) return "";
  if (STAFF_ROLE_KEYS.has(user.role)) {
    const tr = t(`roles.${user.role}`);
    if (tr && tr !== `roles.${user.role}`) return tr;
  }
  if (user.profile_key) {
    const tr = t(`profiles.${user.profile_key}`);
    if (tr && tr !== `profiles.${user.profile_key}`) return tr;
  }
  return user.role || "";
};

const sectionLabel = (pathname, t) => {
  if (pathname.endsWith("/settings")) return t("racketLabApp.section.settings");
  if (pathname.includes("/tickets")) return t("racketLabApp.section.tickets");
  if (pathname.includes("/calendar")) return t("racketLabApp.section.calendar");
  return t("racketLabApp.section.home");
};

const RacketLabTopBar = () => {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const { ctx } = useRLContext();
  const navigate = useNavigate();
  const location = useLocation();
  const role = user?.role;
  const onLogout = () => { logout(); navigate("/"); };

  const isStaff = STAFF_ROLE_KEYS.has(role);
  const scopeLabel = ctx?.organisation_id || ctx?.shop_id || ctx?.profile || "";

  return (
    <header
      className="sticky top-0 z-30 border-b border-slate-200 bg-white/95 backdrop-blur"
      data-testid="rl-app-topbar"
    >
      <div className="flex h-14 items-center justify-between gap-2 px-3 sm:px-4 lg:px-6">
        {/* LEFT — logo + crumb + section */}
        <div className="flex min-w-0 items-center gap-2">
          <div className="shrink-0" data-testid="rl-app-topbar-logo">
            <Logo />
          </div>
          <div className="hidden shrink-0 text-xs uppercase tracking-wide text-slate-400 sm:block" data-testid="rl-app-topbar-crumb">
            <Wrench className="mr-1 inline h-3 w-3 text-emerald-700" />
            Racket Lab /
          </div>
          <div
            className="truncate text-sm font-semibold text-slate-900 sm:text-base"
            data-testid="rl-app-topbar-section"
            title={sectionLabel(location.pathname, t)}
          >
            {sectionLabel(location.pathname, t)}
          </div>
        </div>

        {/* RIGHT — quick links + lang + identity + logout */}
        <div className="flex shrink-0 items-center gap-1 sm:gap-2">
          <NavLink
            to="/"
            className="hidden items-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 sm:inline-flex"
            data-testid="rl-app-topbar-home"
            title={t("common.home")}
          >
            <Home className="h-3.5 w-3.5" />
            <span className="hidden lg:inline">{t("common.home")}</span>
          </NavLink>
          <NavLink
            to="/apps"
            className="hidden items-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 sm:inline-flex"
            data-testid="rl-app-topbar-hub"
            title="TEAM OS Hub"
          >
            <Grid3x3 className="h-3.5 w-3.5" />
            <span className="hidden lg:inline">TEAM OS Hub</span>
          </NavLink>
          <NavLink
            to="/app/racket-lab/tickets"
            className="hidden items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100 md:inline-flex"
            data-testid="rl-app-topbar-tickets"
            title={t("racketLabApp.quick.tickets")}
          >
            <Inbox className="h-3.5 w-3.5" />
            <span className="hidden lg:inline">{t("racketLabApp.quick.tickets")}</span>
          </NavLink>
          <NavLink
            to="/app/racket-lab/calendar"
            className="hidden items-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 md:inline-flex"
            data-testid="rl-app-topbar-calendar"
            title={t("racketLabApp.quick.calendar")}
          >
            <CalendarDays className="h-3.5 w-3.5" />
            <span className="hidden lg:inline">{t("racketLabApp.quick.calendar")}</span>
          </NavLink>
          {isStaff && (
            <NavLink
              to="/admin/racket-lab"
              className="hidden items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 md:inline-flex"
              data-testid="rl-app-topbar-admin"
              title={t("racketLabApp.quick.adminConsole")}
            >
              <ShieldAlert className="h-3.5 w-3.5 text-rose-600" />
              <span className="hidden lg:inline">{t("racketLabApp.quick.adminConsole")}</span>
            </NavLink>
          )}

          <div className="mx-1 hidden h-6 border-l border-slate-200 sm:block" aria-hidden />

          <div data-testid="rl-app-topbar-lang">
            <LanguageDropdown variant="navbar" testIdPrefix="rl-app-topbar-lang" />
          </div>

          {user && (
            <div
              className="hidden max-w-[260px] items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-700 md:flex"
              data-testid="rl-app-topbar-user"
              title={`${user.full_name || user.email} — ${identityRole(user, t)}${scopeLabel ? ` · ${scopeLabel}` : ""}`}
            >
              <UserCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-700" />
              <span className="truncate" data-testid="rl-app-topbar-user-name">
                {user.full_name || user.email}
              </span>
              {identityRole(user, t) && (
                <>
                  <span className="text-slate-300">·</span>
                  <span className="whitespace-nowrap text-emerald-700" data-testid="rl-app-topbar-user-role">
                    {identityRole(user, t)}
                  </span>
                </>
              )}
              {scopeLabel && (
                <>
                  <span className="text-slate-300">·</span>
                  <span className="whitespace-nowrap text-slate-500" data-testid="rl-app-topbar-user-scope">
                    {scopeLabel}
                  </span>
                </>
              )}
            </div>
          )}

          <NavLink
            to="/app/racket-lab/settings"
            className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100"
            data-testid="rl-app-topbar-settings"
            title={t("racketLabApp.quick.settings")}
          >
            <SettingsIcon className="h-3.5 w-3.5" />
            <span className="hidden lg:inline">{t("racketLabApp.quick.settings")}</span>
          </NavLink>

          <button
            type="button"
            onClick={onLogout}
            className="tsw-btn-logout px-3 py-1.5 text-xs"
            data-testid="rl-app-topbar-logout"
            title={t("common.logout")}
          >
            <LogOut className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">{t("common.logout")}</span>
          </button>
        </div>
      </div>

      {/* Preview/QA banner — visible to internal roles only. */}
      {isStaff && (
        <div
          className="border-t border-amber-200 bg-amber-50 px-3 py-1 text-[10px] uppercase tracking-wide text-amber-800 sm:px-4 lg:px-6"
          data-testid="rl-app-topbar-preview-banner"
        >
          Preview / QA · admin & internal roles only · cero customer-facing real · cero notificaciones reales.
        </div>
      )}
    </header>
  );
};

export default RacketLabTopBar;
